/**
 * @prettier
 */

// React Packages
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { environment } from "../../../config/environment";
import { bindActionCreators } from "redux";
import { withTranslation } from "react-i18next";

// Other Packages
import i18next from "i18next";
import axios from "axios";

// Actions
import {
    decrementSidesLeft,
    incrementSidesLeft,
    resetIDProperties,
    setCardOrientation,
    setCardType,
    isExpiredTwoPagePassport,
    clearCapturedDocumentImages,
} from "../../../state/actions/idPropertiesActions";
import {
    setRetryLimit,
    completeStage,
    submissionAttempt,
} from "../../../state/actions/submissionStateActions";
import { clearError } from "../../../state/actions/navigatorActions";

// Services
import imageValidatorService from "../../../services/shared/imageValidatorService";
import documentService from "../../../services/shared/documentService";
import apiService from "../../../services/api/api";
import logService from "../../../services/shared/logService";
import selectStorage from "../../../services/shared/selectStorage";
import {
    areCookiesEnabled,
    isSubmissionComplete,
    getKeyByValue,
    isIdPalCameraEnabled,
} from "../../../services/shared/helpers";
import {
    applyCropSettings,
    getSubmissionType,
    getDocumentTitle,
    applyRetryDocumentUpload,
} from "../../../services/shared/processedDocumentService";

import { makeCommaSeparatedString } from "../../../services/shared/formatting";

// Config
import DataDogService from "../../../services/shared/datadogService";
import { allowedDocumentsTypes, titleMap } from "../../../config/documents";

// Components
import Navigator from "../../../components/Navigator";
import Header from "../../../components/Header";
import DocumentReviewState from "../components/DocumentReviewState";
import FailedVerification from "../components/FailedVerificationState";
import OutOfRetryAttemptsState from "../components/OutOfRetryAttemptsState";
import SubmissionCompleteState from "../components/SubmissionCompleteState";
import DocumentUploadCompletedState from "../components/DocumentUploadCompleteState";
import SubmissionFlowStepper from "../../../components/SubmissionFlowStepper";
import ValidationMessageModals from "../components/ValidationMessageModals";
import ProcessingLayout from "../../../components/ProcessingLayout";
class ProcessedImageResult extends Component {
    constructor(props) {
        super(props);
        this.pageFocusRef = React.createRef();
        this.state = {
            currentDocumentRegion: 0,
            document: {},
            processing: false,
            validation: {
                messages: "",
                validated: false,
            },
            classification: {
                messages: "",
                classified: false,
                detail: "",
            },
            verificationStatus: "",
            callType: "classify",
            failed: false,
            expired: false,
            isAcceptedDoc: true,
            isDocMismatch: false,
            isCountryRestriction: false,
            restrictedCountryName: "",
            navigation: {
                action: "load",
                props: null,
            },
        };
        this.getImageData = this.getImageData.bind(this);
        this.retryDocumentUpload = this.retryDocumentUpload.bind(this);
        this.proceedToNextStep = this.proceedToNextStep.bind(this);

        // Cancels the classification request
        this.CancelToken = axios.CancelToken;
        this.source = this.CancelToken.source();
    }

    /**
     * React component life cycle hook.
     */
    componentDidMount() {
        // Set the callType to display the correct loading messages
        if (this.props.sidesLeft === 2) {
            this.setState({ callType: "classify" });
        } else {
            this.setState({ callType: "verify" });
        }

        // Set document title based on submission state
        const { t } = this.props;
        if (this.props.completed === 1) {
            document.title = t("idpal_doc_title_message_complete");
        }

        if (this.props.completed === 1) {
            return;
        }

        // Sets focus to primary heading on first render
        if (this.pageFocusRef && this.pageFocusRef.current) {
            this.pageFocusRef.current.focus();
        }

        // Record a submission attempt on this side
        this.props.submissionAttempt(
            this.props.sidesLeft === 2 ? "front" : "back"
        );

        // Define document being processed
        const documentId = documentService.findById(
            this.props.location.state.properties.documentId
        );

        // Set state properties
        this.setState({
            processing: false,
            document: documentId,
        });

        // TODO pass correctly formatted data
        // Define image properties required for validation
        const properties = {
            ...this.props.location.state.properties,
            originalImage: this.getImageData(),
            croppedImage: this.props.croppedImage,
            croppedImageWidth: this.props.location.state.width,
            croppedImageHeight: this.props.location.state.height,
            isPassport: this.props.location.state.properties.isPassport,
            document: documentId,
        };

        // Log outcome
        logService.log("Document analysis result from SDK", properties);

        // Back of ID does not need to be classified
        if (this.props.orientation === 1) {
            this.setState({
                classification: {
                    ...this.state.classification,
                    classified: true,
                },
            });
            this.validateUploadedDocument(properties);
        } else {
            this.classifyUploadedDocument(properties, this.source.token);
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { t } = this.props;

        // Set document title based on submission state
        document.title = t(getDocumentTitle(this.state, this.props.completed));

        // Logs verification and classification messages to datadog
        if (this.state.validation.messages !== prevState.validation.messages) {
            if (this.state.validation.messages[0]?.length) {
                DataDogService.log(
                    `Validation Message: ${this.state.validation.messages[0]}`
                );
            }
        }

        if (
            this.state.classification.messages !==
            prevState.classification.messages
        ) {
            DataDogService.log(
                `Classification Message: ${this.state.classification.messages}`
            );
        }

        if (
            this.state.classification.detail !== prevState.classification.detail
        ) {
            DataDogService.log(
                `Classification Call Status: ${this.state.classification.detail}`
            );
        }

        if (this.state.verificationStatus !== prevState.verificationStatus) {
            DataDogService.log(
                `Verification Call Status: ${this.state.verificationStatus}`
            );
        }
    }

    componentWillUnmount() {
        // Cancel request on component dismount
        this.source.cancel("Cancelled");
    }

    /**
     * Classify uploaded document.
     *
     * @param properties
     */
    classifyUploadedDocument(properties, cancelToken) {
        // Clear any error messages
        clearError(this);

        // Get document classification using cropped or uncropped image, based on setting. If no cropped is available, fallback to uncropped
        apiService
            .getDocumentClassification(
                documentService.urlToBlob(
                    this.props.croppedImage &&
                        this.props.classificationCrop === 1
                        ? this.props.croppedImage
                        : this.getImageData()
                ),
                cancelToken
            )
            .then(response => {
                let document = null;

                if (response.data) {
                    // Set document based on classification
                    document = documentService.findByClassification(
                        response.data.classification
                    );

                    // Set classification response to log to datadog
                    this.setState({
                        classification: {
                            ...this.state.classification,
                            detail: response.detail,
                        },
                    });

                    if (document) {
                        // Update card type to the classified card type c
                        this.props.setCardType(document.id);

                        // Check if it is an allowed cardType
                        if (
                            this.props.isDocSelectionEnabled ||
                            this.props.isCountryRestrictionEnabled
                        ) {
                            const classification =
                                response.data.classification || {};
                            const isCountryRestricted =
                                typeof classification.is_country_restricted ===
                                "boolean"
                                    ? classification.is_country_restricted
                                    : false;
                            const restrictedCountry =
                                classification.restricted_country_name || null;
                            const documentSelection = this.isAcceptedDoc(
                                document.name,
                                allowedDocumentsTypes
                            );

                            this.setState({
                                isAcceptedDoc:
                                    documentSelection && !isCountryRestricted,
                                isCountryRestriction: isCountryRestricted,
                                restrictedCountryName: restrictedCountry,
                            });
                        }
                    }
                    // Log outcome
                    logService.log("Document classified.", document);
                } else {
                    // Could not get classification, fall back to selected document
                    document = documentService.findById(
                        this.props.location.state.properties.documentId
                    );

                    logService.log(
                        "Document not classified, fallback to manually selected",
                        document
                    );
                }

                // Only set classification state if we have document or if doc is a paper licence
                if (document || properties.document.name === "Paper Licence") {
                    if (document) {
                        properties.document = document;
                        this.setState({
                            currentDocumentRegion: response.data
                                ? response.data.classification.documentRegion
                                : this.state.currentDocumentRegion,
                        });
                    }

                    this.setState({
                        classification: {
                            ...this.state.classification,
                            classified: true,
                        },
                        document: properties.document,
                    });
                } else {
                    // set error message if document is not classifiable
                    this.setState({
                        classification: {
                            ...this.state.classification,
                            messages: i18next.t("idpal_classification_error"),
                        },
                    });
                }

                // Only validate if classification is successful
                if (this.state.classification.classified) {
                    // Set validation results
                    this.validateUploadedDocument(properties);
                    // Set the callType to verify to display the loading messages
                    this.setState({ callType: "verify" });
                } else {
                    this.setState({
                        processing: false,
                    });
                }
            })
            .catch(error => {
                if (error === "Cancelled") {
                    logService.error(error);
                } else {
                    DataDogService.createError(
                        "Could not retrieve document classification."
                    );
                    this.setState({
                        navigation: {
                            action: "error",
                            props: {
                                retryAction: () =>
                                    this.classifyUploadedDocument(
                                        properties,
                                        cancelToken
                                    ),
                                error: error,
                            },
                        },
                    });
                }
            });
    }

    /**
     * Validating uploaded document.
     *
     * @param properties
     */
    validateUploadedDocument(properties) {
        // Set validation results
        imageValidatorService(properties).then(response => {
            this.setState({
                validation: response,
                processing: false,
            });
        });
    }

    processVerification(response) {
        this.showSpinner();

        // Update the sessionId value in selectStorage(areCookiesEnabled()) if the one is returned from the response
        if (response.data && response.data.hasOwnProperty("sessionid")) {
            selectStorage(areCookiesEnabled()).setItem(
                "sessionid",
                response.data.sessionid
            );
        }

        // Handle documents which require two pages to be submitted
        if (this.state.document.pages.single === false) {
            // Set relevant states
            this.props.decrementSidesLeft();
            this.props.setCardOrientation(1);

            // Capture second document
            if (this.props.sidesLeft === 1) {
                selectStorage(areCookiesEnabled()).setItem(
                    "documentRegion",
                    this.state.currentDocumentRegion
                );
                this.setState({
                    navigation: {
                        action: "next",
                        props: {
                            documentId: this.state.document.id,
                            sidesLeft: this.props.sidesLeft,
                        },
                    },
                });
            } else {
                // Handle UI transitions
                if (
                    response.code === 200 &&
                    response.data.hasOwnProperty("verification")
                ) {
                    if (
                        response.data.verification.status !== "Passed" &&
                        this.props.submissionAttempts.remaining > 0
                    ) {
                        this.setState({
                            expired: response.data.verification.expired,
                        });
                        this.setState({ failed: true });
                        this.setState({
                            verificationStatus: "Failed to verify",
                        });
                        this.hideSpinner();
                    } else {
                        this.documentCheckComplete();
                    }

                    // Clear document region once back image has been uploaded
                    selectStorage(areCookiesEnabled()).removeItem(
                        "documentRegion"
                    );
                } else if (response.code === 202) {
                    // Successful response, but no verification data, accept document and proceed.
                    this.documentCheckComplete();
                } else {
                    this.setState({
                        navigation: {
                            action: "error",
                        },
                    });
                }
            }
        }

        // Handle documents which require only page to be submitted
        if (
            this.state.document.pages.single === true &&
            this.state.document.pages.number === 1
        ) {
            // Set state
            this.props.decrementSidesLeft();

            // Handle UI transitions
            if (
                response.code === 200 &&
                response.data.hasOwnProperty("verification")
            ) {
                // Failed verification, recapture if attempts remaining
                if (
                    response.data.verification.status !== "Passed" &&
                    this.props.submissionAttempts.remaining > 0
                ) {
                    this.setState({
                        expired: response.data.verification.expired,
                    });
                    this.setState({ failed: true });
                    this.setState({ verificationStatus: "Failed to verify" });
                    this.hideSpinner();
                } else {
                    this.documentCheckComplete();
                }
            } else if (
                response.code === 200 &&
                this.state.document.name === "Paper Licence"
            ) {
                // No verification for Paper Licence
                this.documentCheckComplete();
            } else if (response.code === 202) {
                // Successful response, but no verification data, accept document and proceed.
                this.documentCheckComplete();
            } else {
                this.setState({
                    navigation: {
                        action: "error",
                    },
                });
            }
        }
    }

    /**
     * Upload document to the API
     *
     * @param blobData
     * @param cropped
     */
    uploadDocument(blobData, cropped, cancelToken) {
        this.showSpinner();
        clearError(this);

        const documentRegion =
            selectStorage(areCookiesEnabled()).getItem("documentRegion");

        apiService[getSubmissionType(this.state.document)](
            this.props.instanceID,
            this.props.orientation,
            blobData,
            cropped,
            documentRegion,
            cancelToken
        )
            .then(response => {
                this.processVerification(response);
            })
            .catch(error => {
                // catch if user cancelled the request
                if (error === "Cancelled") {
                    logService.error(error);
                } else {
                    DataDogService.createError(
                        "Unable to submit document for validation."
                    );
                    this.hideSpinner();
                    this.setState({
                        navigation: {
                            action: "error",
                            props: {
                                retryAction: () =>
                                    this.uploadDocument(
                                        blobData,
                                        cropped,
                                        cancelToken
                                    ),
                                error: error,
                            },
                        },
                    });
                }
            });
    }

    /**
     * Proceed to next step of user flow.
     */
    proceedToNextStep() {
        const cropped = applyCropSettings(
            this.props.cropSettings,
            this.state.document.name
        );

        const image = documentService.urlToBlob(
            cropped && this.props.croppedImage
                ? this.props.croppedImage
                : this.getImageData()
        );
        this.uploadDocument(image, cropped, this.source.token);
    }

    /**
     * Retry document upload.
     */
    retryDocumentUpload(thisSideOnly = false) {
        // Reset document validation items
        this.setState({
            classification: {
                messages: "",
                classified: false,
            },
            validation: {
                messages: "",
                validated: false,
            },
            expired: false,
            failed: false,
        });

        this.props.clearCapturedDocumentImages();
        this.setState({
            navigation: applyRetryDocumentUpload(
                this.state,
                this.props,
                thisSideOnly
            ),
        });
    }

    /**
     * Document check complete, continue to success or passive liveness
     */
    documentCheckComplete() {
        this.props.setRetryLimit(0);
        this.props.completeStage("document_upload");
        this.props.resetIDProperties();
        this.setState({
            navigation: {
                action: "next",
            },
        });
    }

    /**
     * If uncropped image is too large in height, width, or file-size, use downscaled version
     */
    getImageData() {
        if (
            this.props.location.state.imageSize > environment.maxFileSize ||
            this.props.location.state.originalHeight > environment.maxHeight ||
            this.props.location.state.originalWidth > environment.maxWidth
        ) {
            return this.props.downscaledImage;
        } else {
            return this.props.uncroppedImage;
        }
    }

    /**
     * Show spinner.
     */
    showSpinner() {
        this.setState({ processing: true });
    }

    /**
     * Hide spinner.
     */
    hideSpinner() {
        this.setState({ processing: false });
    }

    isAcceptedDoc(docType, allowedDocumentsTypes) {
        let documentSelection = true;
        if (this.props.isDocSelectionEnabled) {
            const docTypeKey = getKeyByValue(allowedDocumentsTypes, docType);
            documentSelection =
                this.props.acceptedDocuments.includes(docTypeKey);
        }
        this.setState({ isDocMismatch: !documentSelection });
        return documentSelection;
    }

    getCurrentDocName() {
        const { t } = this.props;

        const docTypeKey = getKeyByValue(
            allowedDocumentsTypes,
            this.state.document.name
        );

        // Translate name if possible
        return t(titleMap[docTypeKey]) || this.state.document.name;
    }

    getListOfAcceptedDocumentTitles() {
        const { t } = this.props;

        const acceptedDocuments = this.props.acceptedDocuments.map(
            title => t(titleMap[title]) || title
        );

        return makeCommaSeparatedString(acceptedDocuments, t("idpal_or"));
    }

    /**
     * Render component.
     */
    render() {
        // Set props
        const data = this.props.location.state;
        const cardImage = this.props.croppedImage ?? this.getImageData();
        const isTwoPagePassportPreview =
            isIdPalCameraEnabled(this.props.captureMethod) &&
            ((this.props.cardType === 1 && this.props.onePagePassport === 0) ||
                this.props.expiredTwoPagePassport);

        if (isSubmissionComplete()) {
            return (
                <SubmissionCompleteState companyName={this.props.companyName} />
            );
        }

        // Document upload is already completed
        if (this.props.completed && this.state.navigation.action === "load") {
            return (
                <DocumentUploadCompletedState
                    onContinue={() =>
                        this.setState({ navigation: { action: "next" } })
                    }
                    navigationAction={this.state.navigation.action}
                    navigationProps={this.state.navigation.props}
                />
            );
        }

        // Render loader component (while state is processing)
        if (
            this.state.processing ||
            (!this.state.validation.messages &&
                !this.state.classification.messages)
        ) {
            return (
                <Fragment>
                    <ProcessingLayout
                        documentData={data}
                        callType={this.state.callType}
                        showMessages={true}
                    />
                    <Navigator
                        page={"document_review"}
                        action={this.state.navigation.action}
                        propsToPass={this.state.navigation.props}
                    />
                </Fragment>
            );
        }

        // Render component
        return (
            <div className="root-layout-container processed-image-result-page">
                {/* User Image capture error modals */}
                <ValidationMessageModals
                    classification={this.state.classification}
                    validation={this.state.validation}
                    isAcceptedDoc={this.state.isAcceptedDoc}
                    expired={this.state.expired}
                    failed={this.state.failed}
                    submissionAttempts={this.props.submissionAttempts}
                    allowedDocumentsTypes={this.getListOfAcceptedDocumentTitles()}
                    isDocMismatch={this.state.isDocMismatch}
                    isCountryRestriction={this.state.isCountryRestriction}
                    restrictedCountryName={this.state.restrictedCountryName}
                    currentDocName={this.getCurrentDocName()}
                />

                <Header />
                <SubmissionFlowStepper />
                {/*Normal Submission Process*/}
                {this.props.submissionAttempts.remaining > 0 &&
                    !this.state.failed && (
                        <DocumentReviewState
                            data={data}
                            submissionAttempts={this.props.submissionAttempts}
                            isFailed={this.state.failed}
                            classification={this.state.classification}
                            pageFocusRef={this.pageFocusRef}
                            validation={this.state.validation}
                            cardImage={cardImage}
                            isTwoPagePassport={isTwoPagePassportPreview}
                            isAcceptedDoc={this.state.isAcceptedDoc}
                            proceedToNextStep={this.proceedToNextStep}
                            retryDocumentUpload={this.retryDocumentUpload}
                        />
                    )}

                {/*Submitted ID Failed Verification*/}
                {this.state.failed && (
                    <FailedVerification
                        isExpired={this.state.expired}
                        pageFocusRef={this.pageFocusRef}
                        cardImage={cardImage}
                        retryDocumentUpload={this.retryDocumentUpload}
                    />
                )}

                {/* Out of retry attempts. Submit whatever regardless of validation or classification */}
                {this.props.submissionAttempts.remaining <= 0 && (
                    <OutOfRetryAttemptsState
                        pageFocusRef={this.pageFocusRef}
                        cardImage={cardImage}
                        proceedToNextStep={this.proceedToNextStep}
                        isTwoPagePassport={isTwoPagePassportPreview}
                    />
                )}

                <Navigator
                    page={"document_review"}
                    action={this.state.navigation.action}
                    propsToPass={this.state.navigation.props}
                />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        instanceID: state.config.instanceID,
        orientation: state.idProperties.orientation,
        bypassAcuant: state.config.bypassAcuant,
        cardType: state.idProperties.cardType,
        sidesLeft: state.idProperties.sidesLeft,
        frontSubmitted: state.config.frontSubmitted,
        backSubmitted: state.config.backSubmitted,
        submissionAttempts: state.submissionState.submissionAttempts,
        completed:
            state.submissionState.submissionState.document_upload.completed,
        screens: state.submissionState.screens,
        cropSettings: state.config.cropSettings,
        uncroppedImage: state.idProperties.uncroppedImage,
        downscaledImage: state.idProperties.downscaledImage,
        croppedImage: state.idProperties.croppedImage,
        captureMethod:
            state.submissionState.screens.document_upload.capture_method,
        companyName: state.config.profile.data.company_branding.name,
        onePagePassport:
            state.submissionState.screens.document_upload.one_page_passport,
        expiredTwoPagePassport: state.idProperties.isExpiredTwoPagePassport,
        classificationCrop:
            state.submissionState.screens.document_upload.classification_crop,
        isDocSelectionEnabled: state.submissionState.isDocSelectionEnabled,
        acceptedDocuments: state.config.profile.data.accepted_documents,
        userSelectedCardType: state.idProperties.userSelectedCardType,
        isCountryRestrictionEnabled:
            state.submissionState.isCountryRestrictionEnabled,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            decrementSidesLeft,
            incrementSidesLeft,
            resetIDProperties,
            setCardOrientation,
            setRetryLimit,
            setCardType,
            completeStage,
            submissionAttempt,
            isExpiredTwoPagePassport,
            clearCapturedDocumentImages,
        },
        dispatch
    );
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(ProcessedImageResult)
);
